import React, { useEffect, useMemo, useRef, useState, useLayoutEffect } from "react";
import type { Player, Position } from "../../shared/models/Player";
import type { RankingsListKey, TiersByPos } from "../../shared/utils/xlsxRankings";
import { posColor } from "../../shared/utils/posColor";
import { formatTeamAbbreviation } from "../../shared/utils/teamAbbreviation";

import TierBar, { TierOverlay, type TierScope } from "../../shared/components/Rankings/TierBar";
import Headshot from "../../shared/components/Rankings/Headshot";
import { buildSearchMatches, type SearchMatch } from "../../shared/components/Rankings/searchUtils";
import { StarIcon, type FavoriteStarStyle } from "../../shared/components/Rankings/StarIcon";
import { score10ToPct } from "../../shared/components/Rankings/RiskUpside";
import PlayerProfileModal from "./PlayerProfileModal";
import TeamLogo from "../../shared/components/TeamLogo";
import { playerProfilesById } from "../../shared/data/playerProfiles";
import { playerGameLogsById } from "../../shared/data/playerGameLogs";
import {
  getRiskRaw,
  getUpsideRaw,
  isFiniteNumber,
  type DesktopSourceTabVisibility,
  type Tab,
} from "../features/desktop/rankingsListUtils";
import {
  RankingsEmptyState,
  RankingsSearchSection,
  RankingsSettingsModal,
} from "../features/desktop/rankingsSections";

export default function RankingsList(props: {
  // which rankings set is shown
  rankingsListKey: RankingsListKey;
  setRankingsListKey: React.Dispatch<React.SetStateAction<RankingsListKey>>;

  // data for the active rankings set
  rankingIds: string[];
  // optional: the Rankings ordering (if the app wants to pass it for other components)
  rankingsRankingIds?: string[];
  playersById: Record<string, Player>;

  tiersByPos: TiersByPos;
  draftedIds: Set<string>;
  onToggleDrafted: (id: string) => void;
  hideKickers: boolean;
  setHideKickers: React.Dispatch<React.SetStateAction<boolean>>;
  hideDefenses: boolean;
  setHideDefenses: React.Dispatch<React.SetStateAction<boolean>>;

  favoriteIds: Set<string>;
  onToggleFavorite: (id: string) => void;
  onRemoveAddedPlayer: (id: string) => void;

  activeTab: Tab;
  setActiveTab: (t: Tab) => void;

  // optional hook for parent-managed reordering/moves (backwards compatible)
  onMove?: (fromIndex: number, toIndex: number) => void;

  getColor: (pos: Position) => string;

  onSetAsRankings?: () => void;
  onImportExpertRankings?: () => void;
  onRemoveExpertRankings?: () => void;
  expertRankingIds?: string[];
  uiScale?: number;
}) {
  const {
    rankingsListKey,
    setRankingsListKey,
    rankingIds,
    playersById,
    tiersByPos,
    draftedIds,
    onToggleDrafted,
    hideKickers,
    setHideKickers,
    hideDefenses,
    setHideDefenses,
    favoriteIds,
    onToggleFavorite,
    onRemoveAddedPlayer,
    activeTab,
    setActiveTab,
    getColor,
    onSetAsRankings,
    onImportExpertRankings,
    onRemoveExpertRankings,
    expertRankingIds = [],
    uiScale = 1,
  } = props;

  const [profilePlayerId, setProfilePlayerId] = useState<string | null>(null);
  const [hideDraftedPlayers, setHideDraftedPlayers] = useState(false);
  const [setAsRankingsFeedback, setSetAsRankingsFeedback] = useState(false);
  const [desktopRankingsSettingsOpen, setDesktopRankingsSettingsOpen] = useState(false);
  const [confirmRestartDraftOpen, setConfirmRestartDraftOpen] = useState(false);
  const [confirmRemoveExpertOpen, setConfirmRemoveExpertOpen] = useState(false);
  const [expertInfoOpen, setExpertInfoOpen] = useState(false);
  const [desktopSourceTabVisibility, setDesktopSourceTabVisibility] = useState<DesktopSourceTabVisibility>(() => {
    if (typeof window === "undefined") {
      return { Rankings: true, Experts: false };
    }
    const raw = window.localStorage.getItem("fantasy-board:desktop-source-tab-visibility");
    if (!raw) {
      return { Rankings: true, Experts: false };
    }
    try {
      const parsed = JSON.parse(raw) as Partial<DesktopSourceTabVisibility>;
      return {
        Rankings: true,
        Experts: parsed.Experts === true,
      };
    } catch {
      return { Rankings: true, Experts: false };
    }
  });
  const [rowZoomScale, setRowZoomScale] = useState(() => {
    if (typeof window === "undefined") return 1;
    const saved = Number(window.localStorage.getItem("fantasy-board:rankings-row-zoom"));
    return Number.isFinite(saved) ? Math.max(0.5, Math.min(1.15, saved)) : 1;
  });
  const rootRef = useRef<HTMLDivElement | null>(null);
  const desktopRankingsSettingsRef = useRef<HTMLDivElement | null>(null);
  const desktopTabsRowRef = useRef<HTMLDivElement | null>(null);
  const desktopControlsRowRef = useRef<HTMLDivElement | null>(null);
  const [desktopListScale, setDesktopListScale] = useState(1);
  const [stackDesktopRankingTabs, setStackDesktopRankingTabs] = useState(false);
  const [isDesktopViewport, setIsDesktopViewport] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.innerWidth > 1100;
  });

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || typeof window === "undefined") return;

    const update = () => {
      const isDesktop = window.innerWidth > 1100;
      setIsDesktopViewport(isDesktop);
      if (!isDesktop) {
        setDesktopListScale(1);
        return;
      }

      const width = root.getBoundingClientRect().width;
      if (width <= 0) return;

      const nextScale = Math.max(0.72, Math.min(0.88, width / 520));
      setDesktopListScale((prev) => (Math.abs(prev - nextScale) < 0.01 ? prev : nextScale));
    };

    update();

    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => update());
    ro.observe(root);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!setAsRankingsFeedback) return;
    const timeoutId = window.setTimeout(() => setSetAsRankingsFeedback(false), 1200);
    return () => window.clearTimeout(timeoutId);
  }, [setAsRankingsFeedback]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("fantasy-board:rankings-row-zoom", String(rowZoomScale));
  }, [rowZoomScale]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("fantasy-board:desktop-source-tab-visibility", JSON.stringify(desktopSourceTabVisibility));
  }, [desktopSourceTabVisibility]);

  useEffect(() => {
    if (rankingsListKey === "Rankings") return;
    if (desktopSourceTabVisibility[rankingsListKey]) return;
    setRankingsListKey("Rankings");
  }, [desktopSourceTabVisibility, rankingsListKey, setRankingsListKey]);

  useEffect(() => {
    if (!desktopRankingsSettingsOpen) return;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (desktopRankingsSettingsRef.current?.contains(target)) return;
      setDesktopRankingsSettingsOpen(false);
    };

    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, [desktopRankingsSettingsOpen]);

  useEffect(() => {
    if (!desktopRankingsSettingsOpen) {
      setConfirmRestartDraftOpen(false);
      setConfirmRemoveExpertOpen(false);
    }
  }, [desktopRankingsSettingsOpen]);

  const hideRankColumn = false;
  const showMetricColumn = false;

  // My Rankings tiers are editable elsewhere; imported Expert tiers are visible but fixed.
  const showTiers = activeTab !== "Overall";

  /**
   * Only show Risk/Upside columns if the import actually contains *meaningful* values.
   *
   * Some imports/parsers default missing numeric columns to 0, which would otherwise trick
   * a simple "any finite number" check into showing empty bars for everyone.
   *
   * Assumption: Risk/Upside are 0–10 scores; if the max value is 0, treat the column as absent.
   */
  const riskPresence = useMemo(() => {
    let seenFinite = false;
    let max = -Infinity;
    for (const id of rankingIds) {
      const p = playersById[id];
      if (!p) continue;
      const v = getRiskRaw(p);
      if (!isFiniteNumber(v)) continue;
      seenFinite = true;
      if (v > max) max = v;
    }
    return { seenFinite, max };
  }, [rankingIds, playersById]);

  const upsidePresence = useMemo(() => {
    let seenFinite = false;
    let max = -Infinity;
    for (const id of rankingIds) {
      const p = playersById[id];
      if (!p) continue;
      const v = getUpsideRaw(p);
      if (!isFiniteNumber(v)) continue;
      seenFinite = true;
      if (v > max) max = v;
    }
    return { seenFinite, max };
  }, [rankingIds, playersById]);

  const showRisk = riskPresence.seenFinite && riskPresence.max > 0;
  const showUpside = upsidePresence.seenFinite && upsidePresence.max > 0;
  const enableDesktopPlayerProfiles = isDesktopViewport;

  const presentPositions = useMemo(() => {
    const set = new Set<Position>();
    for (const id of rankingIds) {
      const p = playersById[id];
      if (p) set.add(p.position);
    }
    return set;
  }, [rankingIds, playersById]);

  const tabs: Tab[] = useMemo(() => {
    const base: Tab[] = ["Overall", "QB", "RB", "WR", "TE"];
    if (presentPositions.has("K") && !hideKickers) base.push("K");
    if (presentPositions.has("DST") && !hideDefenses) base.push("DST");
    return base;
  }, [presentPositions, hideKickers, hideDefenses]);

  const isOverall = activeTab === "Overall";

  React.useEffect(() => {
    // If the current tab no longer exists (e.g. import without K/DST), fall back to Overall.
    if (!tabs.includes(activeTab)) setActiveTab("Overall");
  }, [tabs, activeTab, setActiveTab]);

  // Shared scroll container + row refs across ALL tabs (needed for jump-to)
  const containerRef = useRef<HTMLDivElement | null>(null);
  const stickyHeaderRef = useRef<HTMLDivElement | null>(null);
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Search (jump-to, not filter)
  const [query, setQuery] = useState("");
  // Aliases (kept to match fantasy-board naming)
  const q = query;
  const setQ = setQuery;
  const [activeMatchIndex, setActiveMatchIndex] = useState(0);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const highlightTimerRef = useRef<number | null>(null);

  // Build ids for the active tab
  const idsForTabBase = useMemo(() => {
    const seen = new Set<string>();
    const uniq = (arr: string[]) => arr.filter((id) => (seen.has(id) ? false : (seen.add(id), true)));

    if (isOverall) return uniq(rankingIds);
    return uniq(rankingIds.filter((id) => playersById[id]?.position === activeTab));
  }, [isOverall, rankingIds, playersById, activeTab]);

  const idsForTab = useMemo(() => {
    return idsForTabBase.filter((id) => {
      const player = playersById[id];
      if (!player) return false;
      if (hideDraftedPlayers && draftedIds.has(id)) return false;
      if (hideKickers && player.position === "K") return false;
      if (hideDefenses && player.position === "DST") return false;
      return true;
    });
  }, [idsForTabBase, playersById, hideDraftedPlayers, draftedIds, hideKickers, hideDefenses]);

  const originalIndexById = useMemo(() => {
    const next: Record<string, number> = {};
    idsForTabBase.forEach((id, index) => {
      next[id] = index;
    });
    return next;
  }, [idsForTabBase]);

  const restartDraft = React.useCallback(() => {
    draftedIds.forEach((id) => onToggleDrafted(id));
  }, [draftedIds, onToggleDrafted]);

  const tierBreaks = useMemo(() => {
    if (!showTiers) return [];
    if (isOverall) return [];
    return tiersByPos?.[activeTab] ?? [];
  }, [showTiers, isOverall, tiersByPos, activeTab]);

  const visibleTierBreaks = useMemo(() => {
    if (idsForTab.length === 0 || tierBreaks.length === 0) return [];
    const visibleIdSet = new Set(idsForTab);
    return tierBreaks.filter((id) => visibleIdSet.has(id));
  }, [idsForTab, tierBreaks]);

  const matches: SearchMatch[] = useMemo(() => {
    return buildSearchMatches({
      query,
      idsForTab,
      playersById,
      isOverall,
      rankingIds,
      limit: 12,
    });
  }, [query, idsForTab, playersById, isOverall, rankingIds]);

  React.useEffect(() => {
    setActiveMatchIndex(0);
  }, [query, activeTab]);

  function flashHighlight(id: string) {
    if (highlightTimerRef.current) window.clearTimeout(highlightTimerRef.current);
    setHighlightId(id);
    highlightTimerRef.current = window.setTimeout(() => setHighlightId(null), 900);
  }

  function getRelativeOffsetTop(container: HTMLElement, node: HTMLElement) {
    let top = 0;
    let current: HTMLElement | null = node;

    while (current && current !== container) {
      top += current.offsetTop;
      current = current.offsetParent as HTMLElement | null;
    }

    return top;
  }

  function scrollRowToTopVisible(id: string, opts?: { flash?: boolean }) {
    const container = containerRef.current;
    const row = rowRefs.current[id];
    if (!container || !row) return;

    // When we jump-to, the sticky header can cover the row.
    // Offset the scroll by the sticky header height (plus a tiny gap) so the row is fully visible.
    const headerH = stickyHeaderRef.current?.getBoundingClientRect().height ?? 0;
    const gap = 8;
    const nextTop = Math.max(0, getRelativeOffsetTop(container, row) - headerH - gap);

    container.scrollTo({
      top: nextTop,
      behavior: "smooth",
    });

    if (opts?.flash) flashHighlight(id);
  }

  function jumpToPlayer(id: string) {
    scrollRowToTopVisible(id, { flash: true });
    setQuery("");
  }

  // Compute tier bar positions
  const [gapTops, setGapTops] = useState<Record<string, number>>({});
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const next: Record<string, number> = {};
    for (const startId of visibleTierBreaks) {
      const row = rowRefs.current[startId];
      if (!row) continue;
      const top = row.offsetTop;
      next[startId] = top;
    }
    setGapTops(next);
  }, [visibleTierBreaks, idsForTab, activeTab, rankingsListKey]);

  const tierNumById = useMemo(() => {
    const next: Record<string, number> = {};
    if (!showTiers || idsForTab.length === 0) return next;

    let currentTier = 1;
    let previousVisibleOriginalIndex = -1;
    const tierBreakIndices = tierBreaks
      .map((id) => originalIndexById[id])
      .filter((index): index is number => typeof index === "number")
      .sort((a, b) => a - b);

    for (const id of idsForTab) {
      const currentOriginalIndex = originalIndexById[id];
      let crossedBreak = false;
      for (const breakIndex of tierBreakIndices) {
        if (breakIndex > previousVisibleOriginalIndex && breakIndex <= currentOriginalIndex) {
          crossedBreak = true;
          break;
        }
      }

      if (previousVisibleOriginalIndex >= 0 && crossedBreak) {
        currentTier += 1;
      }

      next[id] = currentTier;
      previousVisibleOriginalIndex = currentOriginalIndex;
    }

    return next;
  }, [showTiers, idsForTab, tierBreaks, originalIndexById]);

  const currentScope: TierScope = activeTab === "Overall" ? "OVERALL" : activeTab;

  const showDropdown = query.trim().length > 0 && matches.length > 0;
  const accentGreen = "rgb(34, 197, 94)";
  const tierAccent = currentScope === "OVERALL" ? accentGreen : posColor(currentScope as Position);

  const rankingTabs = useMemo(
    () =>
      [
        { key: "Rankings" as const, ariaLabel: "My Rankings", labelTop: "My", labelBottom: "Rankings" },
        { key: "Experts" as const, ariaLabel: "Expert Rankings", labelTop: "Expert", labelBottom: "Rankings" },
      ],
    []
  );
  const visibleRankingTabs = useMemo(
    () => rankingTabs.filter((tab) => tab.key === "Rankings" || desktopSourceTabVisibility[tab.key]),
    [desktopSourceTabVisibility, rankingTabs]
  );

  // Selected-tab style: neutral translucent overlay (no green)
  const selectedPillBg = "rgba(255,255,255,0.92)";
  const selectedPillBorder = "none";
  const selectedPillShadow = "0 10px 20px rgba(0,0,0,0.22)";

  const gridCols = useMemo(() => {
    const cols: string[] = ["28px", "minmax(0, 3fr)"]; // #, Player
    if (showMetricColumn) cols.push("minmax(36px, 0.8fr)");
    if (showRisk) cols.push("minmax(54px, 1fr)");
    if (showUpside) cols.push("minmax(54px, 1fr)");
    return cols.join(" ");
  }, [showMetricColumn, showRisk, showUpside]);

  const leftGutterPx = 44; // space reserved for + / − button
  const rowCardPaddingPx = 10; // matches row card horizontal padding

  // Favorite Star Positioning (edit these values to fine tune)
  const FAVORITE_STAR: FavoriteStarStyle = {
    leftPx: leftGutterPx + rowCardPaddingPx + 421,
    topPx: 5,
    sizePx: 20,
    borderPx: 1.3,
  };

  // IMPORTANT: We are using inline Tier separators (TIER X) now.
  // Disable the absolute-position TierBar overlay to avoid the extra gray bars.
  const showTierBars = false;
  const panelZoom = Math.max(0.72, Math.min(1.02, desktopListScale * uiScale));
  const rowsZoom = Math.max(0.5, Math.min(1.15, rowZoomScale));

  useLayoutEffect(() => {
    const root = rootRef.current;
    const tabsRow = desktopTabsRowRef.current;
    const controlsRow = desktopControlsRowRef.current;
    if (!root || !tabsRow || !controlsRow || !isDesktopViewport) {
      setStackDesktopRankingTabs(false);
      return;
    }

    const updateWrap = () => {
      const availableWidth = root.getBoundingClientRect().width;
      const controlsWidth = controlsRow.getBoundingClientRect().width;
      const tabsWidth = tabsRow.scrollWidth;
      const gapAllowance = 24;
      const hasManyVisibleTabs = visibleRankingTabs.length >= 3;
      setStackDesktopRankingTabs(hasManyVisibleTabs && tabsWidth + controlsWidth + gapAllowance > availableWidth);
    };

    updateWrap();

    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => updateWrap());
    ro.observe(root);
    ro.observe(tabsRow);
    ro.observe(controlsRow);
    return () => ro.disconnect();
  }, [isDesktopViewport, visibleRankingTabs]);

  return (
    <div
      ref={rootRef}
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        gap: 10,
        zoom: panelZoom,
      }}
    >
      {/* Top controls: actions, rankings tabs, then position tabs */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            flexWrap: stackDesktopRankingTabs ? "wrap" : "nowrap",
            justifyContent: "space-between",
          }}
        >
          <div
            ref={desktopTabsRowRef}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
              minWidth: 0,
              flex: "1 1 420px",
              width: stackDesktopRankingTabs ? "100%" : undefined,
              order: stackDesktopRankingTabs ? 2 : 1,
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 6,
                alignItems: "center",
                flexWrap: "nowrap",
                padding: 4,
                borderRadius: 999,
                border: "1px solid var(--border-0)",
                background: "var(--panel-bg)",
                width: "fit-content",
                maxWidth: "100%",
                minWidth: 0,
                overflowX: "auto",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              {visibleRankingTabs.map(({ key, ariaLabel, labelTop, labelBottom }) => {
                const active = key === rankingsListKey;

                return (
                  <button
                    key={key}
                    onClick={() => setRankingsListKey(key)}
                    aria-label={ariaLabel}
                    title={ariaLabel}
                    style={{
                      border: active ? selectedPillBorder : "none",
                      borderRadius: 999,
                      padding: labelBottom ? "8px 14px" : "10px 14px",
                      fontWeight: 900,
                      fontSize: 13,
                      cursor: "pointer",
                      background: active ? selectedPillBg : "transparent",
                      color: active ? "#0f172a" : "var(--text-1)",
                      whiteSpace: "nowrap",
                      display: "inline-flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: labelBottom ? 2 : 0,
                      minWidth: labelBottom ? 92 : undefined,
                      lineHeight: 1,
                      boxShadow: active ? selectedPillShadow : "none",
                      flexShrink: 0,
                    }}
                  >
                    <span>{labelTop}</span>
                    {labelBottom && <span style={{ fontSize: 12, opacity: 0.95 }}>{labelBottom}</span>}
                  </button>
                );
              })}
            </div>

          </div>

          <div
            ref={desktopControlsRowRef}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexShrink: 0,
              marginLeft: "auto",
              order: 1,
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: 4,
                borderRadius: 999,
                border: "1px solid var(--border-0)",
                background: "var(--panel-bg)",
                flexShrink: 0,
              }}
            >
              <button
                type="button"
                onClick={() => setRowZoomScale((prev) => Math.max(0.5, Number((prev - 0.05).toFixed(2))))}
                aria-label="Zoom out rankings rows"
                title="Zoom out rankings rows"
                style={{
                  border: "none",
                  borderRadius: 999,
                  width: 34,
                  height: 34,
                  background: "transparent",
                  color: "var(--text-1)",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="2" />
                  <path d="M16 16l4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M8.25 11h5.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>

              <button
                type="button"
                onClick={() => setRowZoomScale(1)}
                aria-label="Reset rankings row zoom"
                title="Reset rankings row zoom"
                style={{
                  border: rowZoomScale === 1 ? selectedPillBorder : "none",
                  borderRadius: 999,
                  minWidth: 44,
                  height: 34,
                  padding: "0 10px",
                  background: rowZoomScale === 1 ? selectedPillBg : "transparent",
                  boxShadow: rowZoomScale === 1 ? selectedPillShadow : "none",
                  color: rowZoomScale === 1 ? "#0f172a" : "var(--text-1)",
                  cursor: "pointer",
                  fontSize: 11,
                  fontWeight: 900,
                  flexShrink: 0,
                }}
              >
                {Math.round(rowZoomScale * 100)}%
              </button>

              <button
                type="button"
                onClick={() => setRowZoomScale((prev) => Math.min(1.15, Number((prev + 0.05).toFixed(2))))}
                aria-label="Zoom in rankings rows"
                title="Zoom in rankings rows"
                style={{
                  border: "none",
                  borderRadius: 999,
                  width: 34,
                  height: 34,
                  background: "transparent",
                  color: "var(--text-1)",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="2" />
                  <path d="M16 16l4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M11 8.25v5.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M8.25 11h5.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <button
              type="button"
              onClick={() => setDesktopRankingsSettingsOpen((open) => !open)}
              aria-label="Open rankings settings"
              title="Open rankings settings"
              style={{
                width: 48,
                height: 48,
                borderRadius: 0,
                border: "none",
                background: "transparent",
                color: "var(--text-0)",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "none",
                padding: 0,
                opacity: desktopRankingsSettingsOpen ? 1 : 0.92,
                flexShrink: 0,
              }}
            >
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.757.426 1.757 2.924 0 3.35a1.724 1.724 0 0 0-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 0 0-2.572 1.065c-.426 1.757-2.924 1.757-3.35 0a1.724 1.724 0 0 0-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 0 0-1.065-2.572c-1.757-.426-1.757-2.924 0-3.35a1.724 1.724 0 0 0 1.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065Z"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx="12" cy="12" r="3.25" stroke="currentColor" strokeWidth="1.75" />
              </svg>
            </button>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
            justifyContent: "space-between",
          }}
        >
          {/* Position tabs */}
          <div
            style={{
              boxSizing: "border-box",
              display: "flex",
              gap: 6,
              alignItems: "center",
              padding: 4,
              borderRadius: 999,
              border: "1px solid var(--border-0)",
              background: "var(--panel-bg)",
              width: "fit-content",
              maxWidth: "100%",
              overflowX: "auto",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            {tabs.map((t) => {
              const active = t === activeTab;
              const activeBg = active ? selectedPillBg : "transparent";
              const activeBorder = active ? selectedPillBorder : "none";
              const activeShadow = active ? selectedPillShadow : "none";

              return (
                <button
                  key={t}
                  onClick={() => setActiveTab(t)}
                  style={{
                    border: activeBorder,
                    borderRadius: 999,
                    padding: "8px 12px",
                    fontWeight: 900,
                    fontSize: 13,
                    cursor: "pointer",
                    background: activeBg,
                    boxShadow: activeShadow,
                    color: active ? "#0f172a" : "var(--text-1)",
                  }}
                >
                  {t}
                </button>
              );
            })}
          </div>

        </div>
      </div>

      <RankingsSettingsModal
        desktopRankingsSettingsOpen={desktopRankingsSettingsOpen}
        desktopRankingsSettingsRef={desktopRankingsSettingsRef}
        setDesktopRankingsSettingsOpen={setDesktopRankingsSettingsOpen}
        confirmRestartDraftOpen={confirmRestartDraftOpen}
        setConfirmRestartDraftOpen={setConfirmRestartDraftOpen}
        confirmRemoveExpertOpen={confirmRemoveExpertOpen}
        setConfirmRemoveExpertOpen={setConfirmRemoveExpertOpen}
        expertRankingIds={expertRankingIds}
        hideDraftedPlayers={hideDraftedPlayers}
        setHideDraftedPlayers={setHideDraftedPlayers}
        hideKickers={hideKickers}
        setHideKickers={setHideKickers}
        hideDefenses={hideDefenses}
        setHideDefenses={setHideDefenses}
        rankingTabs={rankingTabs}
        desktopSourceTabVisibility={desktopSourceTabVisibility}
        setDesktopSourceTabVisibility={setDesktopSourceTabVisibility}
        restartDraft={restartDraft}
        onRemoveExpertRankings={onRemoveExpertRankings}
      />

      <RankingsSearchSection
        q={q}
        setQ={setQ}
        activeTab={activeTab}
        showDropdown={showDropdown}
        matches={matches}
        activeMatchIndex={activeMatchIndex}
        setActiveMatchIndex={setActiveMatchIndex}
        jumpToPlayer={jumpToPlayer}
        playersById={playersById}
        draftedIds={draftedIds}
        getColor={getColor}
        hideRankColumn={hideRankColumn}
        rankingsListKey={rankingsListKey}
        onSetAsRankings={onSetAsRankings}
        setAsRankingsFeedback={setAsRankingsFeedback}
        setSetAsRankingsFeedback={setSetAsRankingsFeedback}
      />

      {/* List */}
      <div
        className="rankings-scroll"
        ref={containerRef}
        style={{
          overflow: "auto",
          flex: 1,
          minHeight: 0,
          position: "relative",
          background: "transparent",
          borderRadius: 16,
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {/* Sticky column headers */}
        <div
          ref={stickyHeaderRef}
          style={{
            position: "sticky",
            top: 0,
            zIndex: 30,
            padding: "10px 12px",
            background: "rgb(10, 12, 16)",
            borderBottom: "1px solid rgba(255,255,255,0.10)",
            boxShadow: "none",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: gridCols,
              gap: 6,
              minWidth: 0,
              width: "100%",
              alignItems: "center",
              paddingLeft: leftGutterPx + rowCardPaddingPx,
              paddingRight: rowCardPaddingPx,
              boxSizing: "border-box",
              fontSize: "clamp(10px, 2.2vw, 12px)",
              fontWeight: 900,
              letterSpacing: 0.6,
              color: "rgba(255,255,255,0.72)",
              textTransform: "uppercase",
              userSelect: "none",
            }}
          >
            <div>{hideRankColumn ? "\u00A0" : "#"}</div>
            <div>Player</div>

            {showMetricColumn && (
              <div style={{ textAlign: "center" }}>Value</div>
            )}

            {showRisk && <div style={{ textAlign: "center" }}>Risk</div>}
            {showUpside && <div style={{ textAlign: "center" }}>Upside</div>}
          </div>
        </div>

        {/* tier bars (disabled) */}
        {showTierBars &&
          visibleTierBreaks
            .filter((startId) => idsForTab.indexOf(startId) > 0 && typeof gapTops[startId] === "number")
            .map((startId) => (
              <TierBar key={`bar:${currentScope}:${startId}`} scope={currentScope} startId={startId} topPx={gapTops[startId]} />
            ))}

        {/* NOTE: remove horizontal padding so row background can "bleed" to the container edge */}
        <div style={{ paddingBottom: 10, zoom: rowsZoom, minHeight: "100%", display: "flex", flexDirection: "column" }}>
          {idsForTab.length === 0 ? (
            <RankingsEmptyState
              rankingsListKey={rankingsListKey}
              expertInfoOpen={expertInfoOpen}
              setExpertInfoOpen={setExpertInfoOpen}
              onImportExpertRankings={onImportExpertRankings}
            />
          ) : (
            idsForTab.map((id, idx) => {
              const p = playersById[id];
              if (!p) return null;

              const drafted = draftedIds.has(id);
              const favored = favoriteIds.has(id);
              const visibleOriginalIndex = originalIndexById[id];
              const previousVisibleOriginalIndex = idx === 0 ? -1 : originalIndexById[idsForTab[idx - 1]];
              const showTierHeader =
                showTiers &&
                idx > 0 &&
                tierBreaks.some(
                  (breakId) =>
                    originalIndexById[breakId] > previousVisibleOriginalIndex &&
                    originalIndexById[breakId] <= visibleOriginalIndex
                );

              const isHi = highlightId === id;
              const stableRank = (originalIndexById[id] ?? idx) + 1;
              const removable = id.startsWith("custom-");

              const riskRaw = getRiskRaw(p);
              const upsideRaw = getUpsideRaw(p);

              const riskPct = score10ToPct(riskRaw);
              const upsidePct = score10ToPct(upsideRaw);

              const shouldShowOverlay = showTiers && idx === 0 ? true : showTierHeader;

              // Full-bleed row background (goes to the container edges)
              // Undrafted rows keep the alternating dark theme; drafted rows get a green-tinted highlight.
              const baseRowBg = idx % 2 === 0 ? "rgba(0,0,0,0.78)" : "rgba(35, 35, 35, 0.86)";
              const draftedRowBg = "rgba(34, 197, 94, 0.16)";
              const rowBg = drafted ? draftedRowBg : baseRowBg;

              return (
                <div
                  key={id}
                  ref={(el) => {
                    rowRefs.current[id] = el;
                  }}
                >
                  <div
                    style={{
                      position: "relative",
                      marginBottom: 0,
                      overflow: "visible",

                      // full-bleed row background (goes to the container edges)
                      background: rowBg,

                      // border now wraps the +/− gutter too
                      border: isHi ? "1px solid rgba(34, 197, 94, 0.45)" : "1px solid rgba(255,255,255,0.10)",
                      borderRadius: 0,

                      // include the left gutter area (where the button lives) inside the bordered row
                      paddingLeft: leftGutterPx,
                    }}
                  >
                    {shouldShowOverlay && <TierOverlay label={`TIER ${tierNumById[id] ?? 1}`} accentColor={tierAccent} />}

                    <button
                      type="button"
                      aria-label={drafted ? "Undraft player" : "Draft player"}
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleDrafted(id);
                      }}
                      style={{
                        position: "absolute",
                        left: 10,
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: 36,
                        height: 36,
                        borderRadius: 999,
                        border: "1px solid rgba(255,255,255,0.22)",
                        background: drafted ? "rgba(239, 68, 68, 0.18)" : "rgba(34, 197, 94, 0.18)",
                        color: drafted ? "rgb(239, 68, 68)" : accentGreen,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 900,
                        fontSize: 23,
                        lineHeight: 1,
                        cursor: "pointer",
                        userSelect: "none",
                        zIndex: 6,
                      }}
                    >
                      {drafted ? "−" : "+"}
                    </button>

                    <button
                      type="button"
                      aria-label={favored ? "Unfavorite player" : "Favorite player"}
                      title={favored ? "Unfavorite" : "Favorite"}
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleFavorite(id);
                      }}
                      style={{
                        all: "unset",
                        position: "absolute",
                        right: 10,
                        top: FAVORITE_STAR.topPx,
                        cursor: "pointer",
                        width: FAVORITE_STAR.sizePx,
                        height: FAVORITE_STAR.sizePx,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        userSelect: "none",
                        zIndex: 7,
                        filter: favored ? "drop-shadow(0 2px 6px rgba(0,0,0,0.55))" : "drop-shadow(0 2px 5px rgba(0,0,0,0.45))",
                      }}
                    >
                      <StarIcon
                        filled={favored}
                        sizePx={FAVORITE_STAR.sizePx}
                        borderPx={FAVORITE_STAR.borderPx}
                        color="#fbbf24"
                        outlineColor={favored ? "rgba(0,0,0,0.55)" : "rgba(255,255,255,0.55)"}
                      />
                    </button>

                    {/* Inner content: keeps the same left gutter/padding, but is transparent so the row background can bleed edge-to-edge */}
                    <div
                      style={{
                        padding: "18px 38px 18px 13px",
                        background: "transparent",
                        color: "rgba(255,255,255,0.92)",
                        opacity: drafted ? 0.85 : 1,
                        userSelect: "none",
                        cursor: "default",
                        boxShadow: isHi ? "0 0 0 2px rgba(0,0,0,0.12)" : undefined,
                      }}
                    >
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: gridCols,
                          gap: 8,
                          minWidth: 0,
                          width: "100%",
                          alignItems: "center",
                        }}
                      >
                        <div style={{ fontWeight: 900, color: "rgba(255,255,255,0.75)", fontSize: 17 }}>
                          {hideRankColumn ? "\u00A0" : stableRank}
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: 13, minWidth: 0 }}>
                          <Headshot src={p?.imageUrl} alt={p.name} />

                          <div style={{ minWidth: 0 }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-start",
                                fontWeight: 900,
                                fontSize: "clamp(14px, 3.1vw, 21px)",
                                lineHeight: 1.1,
                                whiteSpace: "normal",
                                overflow: "visible",
                                textOverflow: "clip",
                                wordBreak: "break-word",
                              }}
                            >
                              {enableDesktopPlayerProfiles ? (
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, minWidth: 0 }}>
                                  <button
                                    type="button"
                                    onClick={() => setProfilePlayerId(p.id)}
                                    style={{
                                      position: "relative",
                                      display: "inline-block",
                                      minWidth: 0,
                                      padding: "0 14px 0 0",
                                      margin: 0,
                                      border: "none",
                                      background: "transparent",
                                      color: "inherit",
                                      cursor: "pointer",
                                      textAlign: "left",
                                      font: "inherit",
                                    }}
                                    title={`Open ${p.name} profile`}
                                  >
                                    <span
                                      style={{
                                        display: "inline-block",
                                      }}
                                    >
                                      {p.name}
                                    </span>
                                  </button>
                                  {removable ? (
                                    <button
                                      type="button"
                                      onClick={() => onRemoveAddedPlayer(id)}
                                      aria-label={`Remove ${p.name}`}
                                      title={`Remove ${p.name}`}
                                      style={{
                                        flex: "0 0 auto",
                                        width: 24,
                                        height: 24,
                                        marginLeft: 1,
                                        borderRadius: 999,
                                        border: "1.5px solid rgba(255,255,255,0.46)",
                                        background: "rgba(255,255,255,0.18)",
                                        color: "rgba(255,255,255,0.98)",
                                        fontSize: 14,
                                        fontWeight: 900,
                                        lineHeight: 1,
                                        padding: 0,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                      }}
                                    >
                                      ×
                                    </button>
                                  ) : null}
                                </span>
                              ) : (
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                  <span>{p.name}</span>
                                  {removable ? (
                                    <button
                                      type="button"
                                      onClick={() => onRemoveAddedPlayer(id)}
                                      aria-label={`Remove ${p.name}`}
                                      title={`Remove ${p.name}`}
                                      style={{
                                        flex: "0 0 auto",
                                        width: 24,
                                        height: 24,
                                        marginLeft: 1,
                                        borderRadius: 999,
                                        border: "1.5px solid rgba(255,255,255,0.46)",
                                        background: "rgba(255,255,255,0.18)",
                                        color: "rgba(255,255,255,0.98)",
                                        fontSize: 14,
                                        fontWeight: 900,
                                        lineHeight: 1,
                                        padding: 0,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                      }}
                                    >
                                      ×
                                    </button>
                                  ) : null}
                                </span>
                              )}
                            </div>
                            <div
                              style={{
                                fontSize: "clamp(13px, 2.9vw, 16px)",
                                opacity: 0.75,
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                marginTop: 5,
                              }}
                            >
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                                <span style={{ color: posColor(p.position), fontWeight: 700 }}>{p.position}</span>
                                <span style={{ opacity: 0.55 }}>•</span>
                                <TeamLogo
                                  team={p.team}
                                  size={31}
                                  fallback={<span>{formatTeamAbbreviation(p.team, "--")}</span>}
                                />
                              </span>
                              {drafted && (
                                <span
                                  style={{
                                    fontSize: 13,
                                    fontWeight: 900,
                                    letterSpacing: 0.4,
                                    padding: "3px 10px",
                                    borderRadius: 999,
                                    background: "rgba(240, 234, 234, 0.14)",
                                    color: "rgba(255,255,255,0.86)",
                                  }}
                                >
                                  DRAFTED
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {showMetricColumn && (
                          <div
                            style={{
                              textAlign: "center",
                              fontWeight: 900,
                              fontSize: "clamp(13px, 2.9vw, 16px)",
                              color: "rgba(255,255,255,0.78)",
                            }}
                          >
                            —
                          </div>
                        )}

                        {showRisk && (
                          <div style={{ display: "flex", justifyContent: "center" }}>
                            <div
                              style={{
                                width: "100%",
                                maxWidth: "100%",
                                height: 13,
                                borderRadius: 999,
                                background: "rgba(255,255,255,0.22)",
                                overflow: "hidden",
                              }}
                            >
                              <div style={{ height: "100%", width: `${riskPct}%`, background: "rgb(239, 68, 68)" }} />
                            </div>
                          </div>
                        )}

                        {showUpside && (
                          <div style={{ display: "flex", justifyContent: "center" }}>
                            <div
                              style={{
                                width: "100%",
                                maxWidth: "100%",
                                height: 13,
                                borderRadius: 999,
                                background: "rgba(255,255,255,0.22)",
                                overflow: "hidden",
                              }}
                            >
                              <div style={{ height: "100%", width: `${upsidePct}%`, background: accentGreen }} />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {enableDesktopPlayerProfiles && profilePlayerId && playersById[profilePlayerId] && (
        <PlayerProfileModal
          player={playersById[profilePlayerId]}
          links={playerProfilesById[profilePlayerId] ?? []}
          gameLogs={playerGameLogsById[profilePlayerId] ?? []}
          rankingIds={rankingIds}
          rankingsRankingIds={props.rankingsRankingIds}
          expertRankingIds={expertRankingIds}
          playersById={playersById}
          onClose={() => setProfilePlayerId(null)}
        />
      )}
    </div>
  );
}
